import { Badge } from "flowbite-react";
import { HiChevronDown, HiChevronUp } from "react-icons/hi";
import { useState, useMemo } from "react";

interface UniverseBadgesProps {
  universes: Array<{
    modelsKey: string;
    universe?: string;
    assetsCount?: number;
  }>;
  selectedUniverse: string;
  onUniverseSelect: (universeKey: string) => void;
  initialVisibleCount?: number;
}

export const UniverseBadges = ({
  universes,
  selectedUniverse,
  onUniverseSelect,
  initialVisibleCount = 5,
}: UniverseBadgesProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Reorder universes: selected first, then rest
  const orderedUniverses = useMemo(() => {
    const selected = universes.find((u) => u.modelsKey === selectedUniverse);
    const others = universes.filter((u) => u.modelsKey !== selectedUniverse);
    return selected ? [selected, ...others] : universes;
  }, [universes, selectedUniverse]);

  // Determine visible universes based on expand state
  const visibleUniverses = isExpanded
    ? orderedUniverses
    : orderedUniverses.slice(0, initialVisibleCount);

  const hasMore = orderedUniverses.length > initialVisibleCount;

  return (
    <div className="space-y-3">
      {/* Badges Grid */}
      <div className="flex flex-wrap gap-2">
        {visibleUniverses.map((universe) => {
          const isActive = selectedUniverse === universe.modelsKey;
          const displayName = universe.universe
            ? `${universe.universe} (${universe.assetsCount || 0})`
            : universe.modelsKey;

          return (
            <button
              key={universe.modelsKey}
              onClick={() => onUniverseSelect(universe.modelsKey)}
            >
              <Badge
                color={isActive ? "info" : "gray"}
                size="sm"
                className={isActive ? 'bg-blue-600 text-white hover:bg-blue-600' : ''}
              >
                {displayName}
              </Badge>
            </button>
          );
        })}
      </div>

      {/* Expand/Collapse Button */}
      {hasMore && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 font-medium transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 rounded px-2 py-1"
        >
          {isExpanded ? (
            <>
              <HiChevronUp className="w-4 h-4" />
              <span>Show Less</span>
            </>
          ) : (
            <>
              <HiChevronDown className="w-4 h-4" />
              <span>Show All ({orderedUniverses.length - initialVisibleCount} more)</span>
            </>
          )}
        </button>
      )}
    </div>
  );
};
